import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import {
  Users, Gift, TrendingUp, Clock, Check, X, Trash2, Plus,
  DollarSign, Award, AlertTriangle, ArrowLeft, Info, Download,
  Trophy, Crown, Medal, FileSignature, ScrollText, MoveRight,
  Calendar as CalendarIcon,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { referralAPI } from '@/lib/api';
import { getErrorMessage, isKRRole, formatPhoneInput } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { UserAvatar } from '@/components/common/UserAvatar';

const PRODUCT_LABEL = {
  medicare: 'Medicare',
  aca: 'ACA / Marketplace',
  dental_vision: 'Dental & Vision',
  supplemental: 'Supplemental',
  life: 'Life',
};

const STATUS_TONE = {
  pending_approval: 'bg-amber-100 text-amber-800 border-amber-300',
  active: 'bg-sky-100 text-sky-800 border-sky-300',
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected: 'bg-rose-100 text-rose-800 border-rose-300',
  expired: 'bg-slate-200 text-slate-700 border-slate-300',
};

const LEVEL_TONE = {
  0: 'bg-slate-100 text-slate-600',
  1: 'bg-sky-100 text-sky-800',
  2: 'bg-emerald-100 text-emerald-800',
  3: 'bg-amber-100 text-amber-800',
  4: 'bg-violet-100 text-violet-800',
};

// Kept in sync with backend `services/referral_agreement.RECRUIT_STAGES`.
// Fallback list if the /stages endpoint fails to load — the UI still
// renders the timeline in the correct order.
const DEFAULT_STAGES = [
  { key: 'submitted',        label: 'Submitted',        description: 'Referral registered by the referring agent.' },
  { key: 'contacted',        label: 'Contacted',        description: 'KR reached out to the candidate.' },
  { key: 'interview',        label: 'Interview',        description: 'Candidate interviewed with KR leadership.' },
  { key: 'contract_sent',    label: 'Contract sent',    description: 'Contracting paperwork issued to the candidate.' },
  { key: 'onboarded',        label: 'Onboarded',        description: 'Candidate signed and completed onboarding.' },
  { key: 'active_producing', label: 'Active producing', description: 'Candidate is writing business under KR.' },
];

const ACCEPTANCE_METHODS = [
  { value: 'phone',     label: 'Phone call' },
  { value: 'email',     label: 'Email' },
  { value: 'in_person', label: 'In person' },
  { value: 'whatsapp',  label: 'WhatsApp' },
  { value: 'sms',       label: 'Text / SMS' },
];

// Custom Markdown renderers used to display the KR Referral Program Agreement
// inside the signature modal. Defined at module scope (not inline in the
// dialog) so React does not unmount the entire subtree on every re-render.
const MDX_H1 = ({ node, ...p }) => <h1 className="text-lg font-bold text-[#0f2a5e] mt-1 mb-2" {...p} />;
const MDX_H2 = ({ node, ...p }) => <h2 className="text-sm font-bold text-[#b8862d] uppercase tracking-wider mt-4 mb-1.5" {...p} />;
const MDX_P  = ({ node, ...p }) => <p className="my-2 text-slate-700" {...p} />;
const MDX_STRONG = ({ node, ...p }) => <strong className="text-slate-900 font-semibold" {...p} />;
const MDX_UL = ({ node, ...p }) => <ul className="list-disc pl-5 my-2 space-y-1 text-slate-700" {...p} />;
const MDX_OL = ({ node, ...p }) => <ol className="list-decimal pl-5 my-2 space-y-1 text-slate-700" {...p} />;
const MDX_BLOCKQUOTE = ({ node, ...p }) => <blockquote className="border-l-2 border-amber-300 pl-3 my-2 italic text-slate-600 bg-amber-50/60 py-1" {...p} />;
const MDX_TABLE = ({ node, ...p }) => <table className="w-full border-collapse my-3 text-[12px]" {...p} />;
const MDX_THEAD = ({ node, ...p }) => <thead className="bg-slate-100" {...p} />;
const MDX_TH = ({ node, ...p }) => <th className="border border-slate-200 px-2 py-1 text-left font-semibold text-slate-800" {...p} />;
const MDX_TD = ({ node, ...p }) => <td className="border border-slate-200 px-2 py-1 text-slate-700" {...p} />;
const MDX_CODE = ({ node, ...p }) => <code className="bg-slate-100 px-1 rounded text-[12px]" {...p} />;

const AGREEMENT_MD_COMPONENTS = {
  h1: MDX_H1, h2: MDX_H2, p: MDX_P, strong: MDX_STRONG,
  ul: MDX_UL, ol: MDX_OL, blockquote: MDX_BLOCKQUOTE,
  table: MDX_TABLE, thead: MDX_THEAD, th: MDX_TH, td: MDX_TD, code: MDX_CODE,
};

/* ─── leaderboard card ────────────────────────────────── */
function LeaderboardCard({ leaders, currentNpn }) {
  const podium = leaders.slice(0, 3);
  const rest = leaders.slice(3, 10);

  const podiumTone = ['from-amber-400 to-amber-600', 'from-slate-300 to-slate-500', 'from-orange-400 to-orange-600'];
  const podiumIcon = [Crown, Medal, Trophy];

  return (
    <Card className="overflow-hidden" data-testid="referrals-leaderboard">
      <CardContent className="p-5 bg-gradient-to-br from-[#0f2a5e] to-[#1e3a8a] text-white">
        <div className="flex items-center gap-2 mb-4">
          <Trophy size={16} className="text-amber-300" />
          <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-200">
            Top Referring Agents
          </h3>
          <span className="ml-auto text-[10px] text-slate-300 italic">Live rankings · bonus earned · points</span>
        </div>
        {/* Podium — top 3 */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {podium.map((l, i) => {
            const Icon = podiumIcon[i];
            const isMe = currentNpn && l.npn === currentNpn;
            return (
              <div
                key={l.npn}
                className={`rounded-lg p-3 flex flex-col items-center text-center bg-gradient-to-b ${podiumTone[i]} shadow-lg ${isMe ? 'ring-2 ring-white' : ''}`}
                data-testid={`leaderboard-podium-${i + 1}`}
              >
                <Icon size={16} className="mb-1" />
                {/* UserAvatar auto-falls back to initials on 404/error
                    so a stale/broken photo_url never shows the browser's
                    default broken-image icon. */}
                <UserAvatar
                  name={l.name}
                  photoUrl={l.photo_url}
                  size="xl"
                  ring
                  className="mb-1.5"
                  testId={`leaderboard-avatar-${l.npn}`}
                />
                <div className="text-xs font-semibold truncate w-full" title={l.name}>{l.name}</div>
                <div className="text-[10px] opacity-80">NPN {l.npn}</div>
                <div className="text-sm font-bold mt-1">${(l.earned_bonus_usd || 0).toLocaleString()}</div>
                <div className="text-[10px] opacity-90">{l.live_points} pts · {l.total_referrals} ref</div>
                {isMe && <span className="text-[9.5px] mt-1 uppercase tracking-wider font-bold">You</span>}
              </div>
            );
          })}
          {/* Fillers if fewer than 3 leaders exist yet */}
          {Array.from({ length: Math.max(0, 3 - podium.length) }).map((_, i) => (
            <div key={`filler-${i}`} className="rounded-lg p-3 flex items-center justify-center bg-white/5 border border-white/10 text-white/40 text-xs italic min-h-[130px]">
              Open slot
            </div>
          ))}
        </div>
        {/* Ranks 4-10 */}
        {rest.length > 0 && (
          <div className="rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 divide-y divide-white/10">
            {rest.map((l, i) => {
              const isMe = currentNpn && l.npn === currentNpn;
              return (
                <div
                  key={l.npn}
                  className={`px-3 py-2 flex items-center gap-3 text-xs ${isMe ? 'bg-white/20' : ''}`}
                  data-testid={`leaderboard-row-${i + 4}`}
                >
                  <span className="w-5 text-center font-mono opacity-70">#{i + 4}</span>
                  <UserAvatar name={l.name} photoUrl={l.photo_url} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{l.name}{isMe && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-amber-200">You</span>}</div>
                    <div className="text-[10px] opacity-70">NPN {l.npn} · {l.total_referrals} referrals</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-semibold text-amber-200">${(l.earned_bonus_usd || 0).toLocaleString()}</div>
                    <div className="text-[10px] opacity-70">{l.live_points} pts</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ReferralsPage() {
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const [items, setItems] = useState([]);
  const [rules, setRules] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null); // referral_id
  const [showCreate, setShowCreate] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [leaders, setLeaders] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {};
      const [r, rl, el, lb] = await Promise.all([
        referralAPI.list(params),
        referralAPI.rules(),
        referralAPI.eligibility(),
        referralAPI.leaderboard(10).catch(() => ({ data: { items: [] } })),
      ]);
      setItems(r.data.items || []);
      setRules(rl.data);
      setEligibility(el.data);
      setLeaders(lb.data.items || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load referrals'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  if (selected) {
    return (
      <ReferralDetail
        referralId={selected}
        onBack={() => { setSelected(null); load(); }}
        isKR={isKR}
        rules={rules}
      />
    );
  }

  return (
    <div data-testid="referrals-page">
      <PageHeader
        title="Referral Partner Program"
        description={isKR
          ? "Manage every agent-referral in the program"
          : "Refer a new agent to KR and earn a production-based bonus"}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Referrals', href: '/referrals' },
        ]}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowRules(true)} data-testid="show-rules-btn">
              <Info size={14} className="mr-1.5" /> Program rules
            </Button>
            {isKR && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const params = statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {};
                    const res = await referralAPI.exportCsv(params);
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a');
                    a.href = url; a.download = 'referrals_report.csv';
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('Report downloaded');
                  } catch (e) {
                    toast.error(getErrorMessage(e, 'Export failed'));
                  }
                }}
                data-testid="export-referrals-csv-btn"
              >
                <Download size={14} className="mr-1.5" /> Export CSV
              </Button>
            )}
            {eligibility?.eligible && (
              <Button onClick={() => setShowCreate(true)} className="bg-[#0f2a5e] hover:bg-[#0a1f47]" data-testid="new-referral-btn">
                <Plus size={14} className="mr-1.5" /> Refer an agent
              </Button>
            )}
          </div>
        }
      />

      <div className="page-content space-y-4">
        {eligibility && !eligibility.eligible && (
          <Card>
            <CardContent className="p-4 bg-amber-50 border-l-4 border-amber-400 flex items-start gap-3">
              <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-900">
                <strong>You are not eligible to submit new referrals.</strong>
                <div className="text-xs mt-1">
                  {eligibility.message || 'The Referral Partner Program is reserved for agents contracted directly with KR Enterprise Group.'}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {leaders.length > 0 && (
          <LeaderboardCard leaders={leaders} currentNpn={user?.agent_npn || user?.npn} />
        )}
        <div className="flex items-center gap-3">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-56" data-testid="referral-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending_approval">Pending approval</SelectItem>
              <SelectItem value="active">Active (in 75-day window)</SelectItem>
              <SelectItem value="completed">Completed / Paid</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="expired">Expired (no bonus)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="py-16 text-center text-slate-500 text-sm">Loading referrals…</p>
        ) : items.length === 0 ? (
          <Card><CardContent className="p-10 text-center">
            <Gift size={32} className="mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-600 mb-4">No referrals yet.</p>
            {eligibility?.eligible && (
              <Button onClick={() => setShowCreate(true)} className="bg-[#0f2a5e] hover:bg-[#0a1f47]">
                <Plus size={14} className="mr-1.5" /> Submit your first referral
              </Button>
            )}
          </CardContent></Card>
        ) : (
          <div className="grid gap-3">
            {items.map((r) => (
              <ReferralCard
                key={r.referral_id}
                r={r}
                onOpen={() => setSelected(r.referral_id)}
                showReferrer={isKR}
              />
            ))}
          </div>
        )}
      </div>

      <CreateReferralDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onCreated={() => { setShowCreate(false); load(); }}
        isKR={isKR}
        currentUserName={user?.full_name || user?.name || user?.email || ''}
      />
      <RulesDialog open={showRules} onOpenChange={setShowRules} rules={rules} />
    </div>
  );
}

/* ─── card ─────────────────────────────────────────────── */
function ReferralCard({ r, onOpen, showReferrer }) {
  const s = r.summary || {};
  const days = r.days_remaining;
  const statusTone = STATUS_TONE[r.status] || 'bg-slate-100 text-slate-600';
  const levelTone = LEVEL_TONE[s.level ?? 0];

  return (
    <Card
      onClick={onOpen}
      className="hover:shadow-md cursor-pointer transition-shadow"
      data-testid={`referral-card-${r.referral_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Users size={14} className="text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-900 truncate">
                {r.referred_first_name} {r.referred_last_name}
              </h3>
              <Badge variant="outline" className={`text-[10px] ${statusTone}`}>
                {r.status.replace(/_/g, ' ')}
              </Badge>
            </div>
            <div className="text-xs text-slate-500 truncate">{r.referred_email} · {r.referred_state}</div>
            {showReferrer && (
              <div className="text-xs text-slate-500 mt-1">
                <span className="text-slate-400">Referrer:</span> {r.referrer_name} · NPN {r.referrer_npn}
              </div>
            )}
            {r.submitted_on_behalf && (
              <div className="text-[10.5px] mt-1 inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded bg-violet-50 text-violet-800 border border-violet-200" data-testid={`ref-on-behalf-${r.referral_id}`}>
                <Info size={10} />
                Registered by admin:
                <UserAvatar userId={r.submitted_by_user_id} name={r.submitted_by_name} size="xs" />
                <span>{r.submitted_by_name || '—'}</span>
              </div>
            )}
          </div>
          <div className="text-right space-y-1 flex-shrink-0">
            <Badge className={`text-[10.5px] ${levelTone}`}>
              <Award size={11} className="mr-1" />
              Level {s.level ?? 0} · {s.points ?? 0} pts
            </Badge>
            <div className="text-lg font-semibold text-emerald-700">
              ${s.capped_bonus_usd?.toLocaleString?.() ?? '0'}
            </div>
            {r.status === 'active' && days !== null && (
              <div className="text-[11px] text-slate-500 flex items-center gap-1 justify-end">
                <Clock size={11} />
                {days > 0 ? `${days} days left` : days === 0 ? 'Closes today' : `Overdue by ${-days} days`}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── create dialog ──────────────────────────────────── */
function CreateReferralDialog({ open, onOpenChange, onCreated, isKR, currentUserName }) {
  // Two steps:
  //   1) form  — referred candidate details (+ credit-to picker for KR admin)
  //   2) sign  — full agreement text + electronic signature capture
  const [step, setStep] = useState('form');
  const [form, setForm] = useState({
    referred_first_name: '', referred_last_name: '', referred_email: '',
    referred_phone: '', referred_state: '', referred_npn: '',
    is_already_licensed: false, notes: '', on_behalf_of_npn: '',
  });
  const [agreementAck, setAgreementAck] = useState(false);
  const [signature, setSignature] = useState('');
  const [onBehalfAck, setOnBehalfAck] = useState(false);
  const [onBehalfMethod, setOnBehalfMethod] = useState('');
  const [onBehalfNotes, setOnBehalfNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [eligibleAgents, setEligibleAgents] = useState([]);
  const [agentSearch, setAgentSearch] = useState('');
  const [agreement, setAgreement] = useState(null); // { version, markdown }
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  const reset = () => {
    setStep('form');
    setForm({
      referred_first_name: '', referred_last_name: '', referred_email: '',
      referred_phone: '', referred_state: '', referred_npn: '',
      is_already_licensed: false, notes: '', on_behalf_of_npn: '',
    });
    setAgreementAck(false); setSignature('');
    setOnBehalfAck(false); setOnBehalfMethod(''); setOnBehalfNotes('');
    setScrolledToEnd(false);
  };

  // Load agreement text + agent roster when the dialog first opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await referralAPI.agreement();
        if (!cancelled) setAgreement(data);
      } catch {
        // Non-blocking — the sign step still renders a scroll placeholder
      }
    })();
    if (isKR) {
      (async () => {
        try {
          const { data } = await referralAPI.eligibleReferrers();
          if (!cancelled) setEligibleAgents(data.items || []);
        } catch {
          if (!cancelled) setEligibleAgents([]);
        }
      })();
    }
    return () => { cancelled = true; };
  }, [open, isKR]);

  // Pre-fill signature with the referring agent's own name when we know it —
  // agents still have to type it themselves to actually enable Submit, but
  // showing them the exact expected string reduces friction and errors.
  useEffect(() => {
    if (!open || step !== 'sign') return;
    if (isKR && !signature && currentUserName) {
      // Currently we do NOT auto-fill so the admin must actively sign; keep
      // this hook for future personalization (placeholder shows expected name).
    }
  }, [step, open, isKR, signature, currentUserName]);

  const filteredAgents = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    if (!q) return eligibleAgents.slice(0, 50);
    return eligibleAgents.filter((a) =>
      (a.name || '').toLowerCase().includes(q)
      || (a.npn || '').toLowerCase().includes(q)
      || (a.email || '').toLowerCase().includes(q)
    ).slice(0, 50);
  }, [eligibleAgents, agentSearch]);

  const selectedAgent = useMemo(
    () => eligibleAgents.find((a) => a.npn === form.on_behalf_of_npn),
    [eligibleAgents, form.on_behalf_of_npn]
  );

  const isOnBehalf = isKR && !!form.on_behalf_of_npn;

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!isKR) delete payload.on_behalf_of_npn;
      if (!payload.on_behalf_of_npn) delete payload.on_behalf_of_npn;
      payload.agreement_accepted = true;
      payload.agreement_signature = signature.trim();
      payload.agreement_version = agreement?.version || '1.0';
      if (isOnBehalf) {
        payload.on_behalf_agent_accepted = true;
        payload.on_behalf_acceptance_method = onBehalfMethod;
        if (onBehalfNotes.trim()) payload.on_behalf_acceptance_notes = onBehalfNotes.trim();
      }
      await referralAPI.create(payload);
      toast.success(
        isOnBehalf
          ? `Referral registered on behalf of ${selectedAgent?.name || 'agent'}`
          : 'Referral submitted — KR admin will review shortly'
      );
      reset();
      onCreated();
    } catch (e) {
      const err = e?.response?.data?.detail;
      if (err && typeof err === 'object') {
        const code = err.code || '';
        if (code === 'duplicate_referral') {
          toast.error('This candidate is already in the referral pipeline');
        } else if (code === 'on_behalf_target_not_direct_kr_agent') {
          toast.error(err.reason || 'The selected agent is not eligible.');
        } else if (err.reason) {
          toast.error(err.reason);
        } else {
          toast.error(getErrorMessage(e, 'Submission failed'));
        }
      } else {
        toast.error(getErrorMessage(e, 'Submission failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Step-1 validation — same rules as before.
  const canProceedToSign = form.referred_first_name.trim() && form.referred_last_name.trim()
    && form.referred_email.trim() && form.referred_phone.trim() && form.referred_state.trim()
    && (!isKR || form.on_behalf_of_npn); // KR must pick who they're crediting

  // Step-2 validation — signature typed, box ticked, agreement text
  // actually scrolled through (so the agent cannot bypass the terms).
  // For on-behalf submissions the admin must also certify + pick a method.
  const canSubmit = agreementAck
    && signature.trim().length >= 3
    && scrolledToEnd
    && (!isOnBehalf || (onBehalfAck && onBehalfMethod));

  const handleScroll = (e) => {
    const el = e.currentTarget;
    // Give a generous 24-px slack so slight zoom/DPI variance still
    // registers as "reached the bottom".
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setScrolledToEnd(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        className={step === 'sign' ? 'max-w-3xl' : 'max-w-lg'}
        data-testid="create-referral-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 'sign' && <FileSignature size={16} className="text-[#0f2a5e]" />}
            {step === 'form'
              ? (isKR ? 'Register Referral on Behalf of Agent' : 'Refer an Agent')
              : (isOnBehalf ? 'Program Agreement — Admin Certification' : 'Program Agreement — Electronic Signature')}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {step === 'form' ? (
              isKR ? (
                <>Pick the referring agent to credit, then enter the candidate&apos;s details. Next step captures the signed program agreement.</>
              ) : (
                <>The referred agent must be NEW to KR Enterprise. You will earn a bonus based on their production during the first 75 days after activation.</>
              )
            ) : (
              isOnBehalf ? (
                <>Read through the Program Agreement, certify that you explained the terms to <strong>{selectedAgent?.name || 'the agent'}</strong>, and record how they accepted.</>
              ) : (
                <>Please read every section of the Referral Partner Program terms. Scroll to the end, tick the acknowledgment, and type your full name to sign electronically.</>
              )
            )}
          </DialogDescription>
        </DialogHeader>

        {/* ─── STEP 1: Referred candidate details ─── */}
        {step === 'form' && (
          <>
            {isKR && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                <Label className="text-xs font-semibold text-amber-900 flex items-center gap-1">
                  <Users size={12} /> Credit this referral to
                </Label>
                <Input
                  placeholder="Search agent by name, NPN, or email…"
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                  data-testid="ref-agent-search"
                />
                <div
                  className="max-h-40 overflow-y-auto bg-white rounded border border-amber-200 divide-y divide-slate-100"
                  data-testid="ref-agent-list"
                >
                  {filteredAgents.length === 0 ? (
                    <div className="px-3 py-6 text-xs text-slate-400 text-center italic">
                      {eligibleAgents.length === 0 ? 'Loading eligible agents…' : 'No matches. Try a different search.'}
                    </div>
                  ) : filteredAgents.map((a) => (
                    <button
                      key={a.npn}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, on_behalf_of_npn: a.npn }))}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-amber-50 transition-colors ${form.on_behalf_of_npn === a.npn ? 'bg-amber-100 font-semibold' : ''}`}
                      data-testid={`ref-agent-option-${a.npn}`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{a.name}</span>
                        <span className="text-[10px] text-slate-400 font-mono">NPN {a.npn}</span>
                      </div>
                      {a.email && (
                        <div className="text-[10px] text-slate-500">{a.email}</div>
                      )}
                    </button>
                  ))}
                </div>
                {selectedAgent && (
                  <div className="text-[11px] text-amber-900 bg-amber-100 rounded px-2 py-1 flex items-center gap-1">
                    <Check size={12} /> Crediting <strong className="mx-1">{selectedAgent.name}</strong> (NPN {selectedAgent.npn})
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 py-2">
              <div>
                <Label className="text-xs">First name *</Label>
                <Input value={form.referred_first_name} onChange={(e) => setForm(f => ({ ...f, referred_first_name: e.target.value }))} data-testid="ref-first-name" />
              </div>
              <div>
                <Label className="text-xs">Last name *</Label>
                <Input value={form.referred_last_name} onChange={(e) => setForm(f => ({ ...f, referred_last_name: e.target.value }))} data-testid="ref-last-name" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={form.referred_email} onChange={(e) => setForm(f => ({ ...f, referred_email: e.target.value }))} data-testid="ref-email" />
              </div>
              <div>
                <Label className="text-xs">Phone *</Label>
                <Input value={formatPhoneInput(form.referred_phone)} onChange={(e) => setForm(f => ({ ...f, referred_phone: e.target.value }))} data-testid="ref-phone" />
              </div>
              <div>
                <Label className="text-xs">State (2-letter) *</Label>
                <Input maxLength={2} value={form.referred_state} onChange={(e) => setForm(f => ({ ...f, referred_state: e.target.value.toUpperCase() }))} data-testid="ref-state" />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Checkbox id="ref-licensed" checked={form.is_already_licensed} onCheckedChange={(v) => setForm(f => ({ ...f, is_already_licensed: !!v }))} data-testid="ref-licensed" />
                <Label htmlFor="ref-licensed" className="text-xs cursor-pointer">Already licensed</Label>
              </div>
              {form.is_already_licensed && (
                <div className="col-span-2">
                  <Label className="text-xs">NPN (if licensed)</Label>
                  <Input value={form.referred_npn} onChange={(e) => setForm(f => ({ ...f, referred_npn: e.target.value }))} data-testid="ref-npn" />
                </div>
              )}
              <div className="col-span-2">
                <Label className="text-xs">Notes (optional)</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder={isKR ? 'Context for the file (e.g. verbal referral over the phone on Aug 20)' : 'How do you know them? Any context for KR.'} data-testid="ref-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
              <Button
                onClick={() => setStep('sign')}
                disabled={!canProceedToSign}
                className="bg-[#0f2a5e] hover:bg-[#0a1f47]"
                data-testid="ref-next-to-agreement-btn"
              >
                Review Agreement <MoveRight size={14} className="ml-1.5" />
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ─── STEP 2: Program Agreement + Signature ─── */}
        {step === 'sign' && (
          <>
            {/* Header meta strip */}
            <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-500 border-b border-slate-100 pb-2">
              <ScrollText size={13} />
              <span>Version <strong>{agreement?.version || '1.0'}</strong></span>
              <span className="text-slate-300">·</span>
              <span>Signed on <strong>{new Date().toLocaleDateString()}</strong></span>
              {isOnBehalf && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 text-[10px] font-semibold">Admin on-behalf</span>
                </>
              )}
            </div>

            {/* Scrollable agreement text — must be scrolled to the end
                before the signature form becomes usable. */}
            <div
              onScroll={handleScroll}
              className="max-h-[42vh] overflow-y-auto rounded border border-slate-200 bg-slate-50 p-4 text-[13px] leading-relaxed"
              data-testid="agreement-scroll-region"
            >
              {agreement ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={AGREEMENT_MD_COMPONENTS}>{agreement.markdown}</ReactMarkdown>
              ) : (
                <p className="text-slate-500 italic">Loading agreement text…</p>
              )}
              <div className="text-[11px] text-slate-400 italic mt-3">— End of agreement —</div>
            </div>
            {!scrolledToEnd && (
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-1.5">
                <Info size={12} /> Please scroll to the end of the agreement to enable the signature.
              </div>
            )}

            {/* On-behalf admin certification */}
            {isOnBehalf && (
              <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 space-y-2" data-testid="on-behalf-block">
                <div className="text-xs font-semibold text-violet-900 flex items-center gap-1.5">
                  <Users size={12} /> Admin certification for {selectedAgent?.name}
                </div>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="onbehalf-ack"
                    checked={onBehalfAck}
                    onCheckedChange={(v) => setOnBehalfAck(!!v)}
                    data-testid="onbehalf-ack-checkbox"
                  />
                  <Label htmlFor="onbehalf-ack" className="text-xs text-violet-900 cursor-pointer leading-snug">
                    I confirm I explained the Referral Program terms to {selectedAgent?.name || 'the agent'} and they accepted.
                  </Label>
                </div>
                <div>
                  <Label className="text-[11px] text-violet-900">How did the agent accept? *</Label>
                  <Select value={onBehalfMethod} onValueChange={setOnBehalfMethod}>
                    <SelectTrigger className="bg-white" data-testid="onbehalf-method-select">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCEPTANCE_METHODS.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] text-violet-900">Notes (optional)</Label>
                  <Textarea
                    rows={2}
                    value={onBehalfNotes}
                    onChange={(e) => setOnBehalfNotes(e.target.value)}
                    placeholder="e.g. call log excerpt, date/time of the WhatsApp confirmation…"
                    className="bg-white"
                    data-testid="onbehalf-notes"
                  />
                </div>
              </div>
            )}

            {/* Acknowledgment + signature */}
            <div className="rounded-lg border-2 border-[#0f2a5e]/20 bg-white p-3 space-y-2">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="agreement-ack"
                  checked={agreementAck}
                  onCheckedChange={(v) => setAgreementAck(!!v)}
                  disabled={!scrolledToEnd}
                  data-testid="agreement-ack-checkbox"
                />
                <Label htmlFor="agreement-ack" className="text-xs cursor-pointer leading-snug">
                  {isOnBehalf
                    ? 'I have read and understood the KR Referral Partner Program Agreement and confirm the referring agent has accepted its terms.'
                    : 'I have read and understood the KR Referral Partner Program Agreement and agree to all its terms and conditions.'}
                </Label>
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  <FileSignature size={12} /> Electronic signature (type your full name) *
                </Label>
                <Input
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder={isOnBehalf ? currentUserName || 'Admin full name' : 'Your full name'}
                  className="font-serif text-lg italic"
                  data-testid="agreement-signature-input"
                />
                <div className="text-[10.5px] text-slate-500 mt-1">
                  By typing your name and clicking Submit, you agree this is your electronic signature and has the same legal effect as a handwritten signature.
                </div>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="ghost" onClick={() => setStep('form')} disabled={saving} className="sm:mr-auto">
                <ArrowLeft size={14} className="mr-1.5" /> Back
              </Button>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={saving || !canSubmit}
                className="bg-[#0f2a5e] hover:bg-[#0a1f47]"
                data-testid="submit-referral-btn"
              >
                {saving ? 'Submitting…' : (isOnBehalf ? 'Sign & Register on Behalf' : 'Sign & Submit Referral')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── rules dialog ───────────────────────────────────── */
function RulesDialog({ open, onOpenChange, rules }) {
  if (!rules) return null;
  const products = Object.keys(rules.points_per_policy || {});
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="rules-dialog">
        <DialogHeader>
          <DialogTitle>Program Rules — KR Referral Partner Program</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-3 rounded border border-slate-200 bg-slate-50">
              <div className="text-[10px] uppercase text-slate-500">Qualification Window</div>
              <div className="text-xl font-semibold">{rules.qualification_window_days} days</div>
            </div>
            <div className="p-3 rounded border border-slate-200 bg-slate-50">
              <div className="text-[10px] uppercase text-slate-500">Minimum to earn</div>
              <div className="text-xl font-semibold">{rules.min_points_for_bonus} pts</div>
            </div>
            <div className="p-3 rounded border border-slate-200 bg-slate-50">
              <div className="text-[10px] uppercase text-slate-500">Cap per referral</div>
              <div className="text-xl font-semibold">${rules.bonus_cap_usd}</div>
            </div>
          </div>

          <h4 className="text-xs uppercase tracking-wider font-semibold text-[#b8862d] mt-3">Points per policy</h4>
          <div className="grid grid-cols-2 gap-1.5 text-xs">
            {products.map(p => (
              <div key={p} className="flex justify-between px-2 py-1 rounded bg-slate-50">
                <span>{PRODUCT_LABEL[p] || p}</span>
                <span className="font-mono">{rules.points_per_policy[p]}</span>
              </div>
            ))}
          </div>

          <h4 className="text-xs uppercase tracking-wider font-semibold text-[#b8862d] mt-3">Bonus rate ($ per active policy)</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-1.5 pr-3">Product</th>
                  {[1, 2, 3, 4].map(l => <th key={l} className="text-right py-1.5 px-2">L{l}</th>)}
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p} className="border-b border-slate-100">
                    <td className="py-1.5 pr-3">{PRODUCT_LABEL[p] || p}</td>
                    {[1, 2, 3, 4].map(l => (
                      <td key={l} className="text-right py-1.5 px-2 font-mono">
                        ${rules.bonus_rate_table[l]?.[p] ?? 0}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Level thresholds: L1 = 25-49 pts · L2 = 50-74 · L3 = 75-99 · L4 = 100+.
            Only active, issued, in-force policies count. Cancellations, chargebacks
            and lapses are excluded per program terms.
          </p>
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── detail page ────────────────────────────────────── */
function ReferralDetail({ referralId, onBack, isKR, rules }) {
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stages, setStages] = useState(DEFAULT_STAGES);
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showAddPolicy, setShowAddPolicy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approvalDate, setApprovalDate] = useState('');
  const [approving, setApproving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await referralAPI.get(referralId);
      setR(res.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load referral'));
      onBack();
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [referralId]);
  // Load the stages list once; fall back to DEFAULT_STAGES if it fails.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await referralAPI.stages();
        if (!cancelled && data.items?.length) setStages(data.items);
      } catch { /* keep default */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const updateStage = async (stage, note) => {
    const res = await referralAPI.updateStage(referralId, { stage, note });
    setR(res.data);
  };

  const doApprove = async () => {
    setApproving(true);
    try {
      await referralAPI.approve(referralId, approvalDate ? { activation_date: approvalDate } : {});
      toast.success('Referral approved — 75-day clock started');
      setShowApprove(false);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Approve failed'));
    } finally {
      setApproving(false);
    }
  };
  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error('Reason is required'); return; }
    setRejecting(true);
    try {
      await referralAPI.reject(referralId, { reason: rejectReason.trim() });
      toast.success('Referral rejected');
      setShowReject(false);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Reject failed'));
    } finally {
      setRejecting(false);
    }
  };
  const doFinalize = async () => {
    if (!window.confirm('Finalize this referral? This will lock the totals and create the bonus payment.')) return;
    setFinalizing(true);
    try {
      await referralAPI.finalize(referralId);
      toast.success('Referral finalized · bonus payment created in Accounting');
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Finalize failed'));
    } finally {
      setFinalizing(false);
    }
  };
  const togglePolicy = async (p) => {
    try {
      await referralAPI.updatePolicy(referralId, p.policy_id, { active: !p.active });
      load();
    } catch (e) { toast.error(getErrorMessage(e, 'Update failed')); }
  };
  const removePolicy = async (p) => {
    if (!window.confirm('Remove this policy from the referral?')) return;
    try {
      await referralAPI.deletePolicy(referralId, p.policy_id);
      load();
    } catch (e) { toast.error(getErrorMessage(e, 'Remove failed')); }
  };

  if (loading || !r) return <p className="py-16 text-center text-sm text-slate-500">Loading…</p>;

  const s = r.summary || {};
  const days = r.days_remaining;

  return (
    <div data-testid="referral-detail-page">
      <PageHeader
        title={`${r.referred_first_name} ${r.referred_last_name}`}
        description={`Referred by ${r.referrer_name} · ${r.referred_state} · ${r.referred_email}`}
        actions={<Button variant="ghost" onClick={onBack}><ArrowLeft size={14} className="mr-1" /> Back</Button>}
      />
      <div className="page-content grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Summary */}
          <Card><CardContent className="p-5">
            <div className="grid grid-cols-4 gap-3 text-center">
              <Stat icon={<TrendingUp size={16} />} label="Points" value={s.points ?? 0} />
              <Stat icon={<Award size={16} />} label="Level" value={s.level ?? 0} />
              <Stat icon={<DollarSign size={16} />} label="Bonus (raw)" value={`$${s.raw_bonus_usd?.toLocaleString?.() || 0}`} />
              <Stat icon={<Gift size={16} />} label="Bonus (capped)" value={`$${s.capped_bonus_usd?.toLocaleString?.() || 0}`} highlight />
            </div>
            {r.status === 'active' && days !== null && (
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded px-3 py-2">
                <Clock size={14} />
                {days > 0 ? `${days} days remaining in the qualification window` : days === 0 ? 'Window closes today' : `Window closed ${-days} days ago — ready to finalize`}
              </div>
            )}
            {s.min_points_gap > 0 && r.status === 'active' && (
              <div className="mt-2 flex items-center gap-2 text-xs text-amber-800 bg-amber-50 rounded px-3 py-1.5">
                <AlertTriangle size={12} />
                Needs {s.min_points_gap} more points to unlock ANY bonus (minimum {rules?.min_points_for_bonus ?? 25} pts).
              </div>
            )}
          </CardContent></Card>

          {/* Policies */}
          <Card><CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#b8862d]">Policies produced</h3>
              {isKR && r.status === 'active' && (
                <Button size="sm" variant="outline" onClick={() => setShowAddPolicy(true)} data-testid="add-policy-btn">
                  <Plus size={12} className="mr-1" /> Add policy
                </Button>
              )}
            </div>
            {(!r.policies || r.policies.length === 0) ? (
              <p className="text-sm text-slate-500 py-4 text-center">No policies attributed yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {r.policies.map(p => (
                  <li key={p.policy_id} className="py-2.5 flex items-center justify-between text-sm" data-testid={`policy-row-${p.policy_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900">{PRODUCT_LABEL[p.product] || p.product}</div>
                      <div className="text-xs text-slate-500">
                        {p.member_name || '—'} {p.policy_number ? `· #${p.policy_number}` : ''} · {p.issued_at?.slice(0, 10)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={p.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}>
                        {p.active ? 'Active' : 'Inactive'}
                      </Badge>
                      {isKR && r.status === 'active' && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => togglePolicy(p)} className="h-7 text-[11px]">
                            {p.active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => removePolicy(p)} className="h-7 text-rose-600 hover:text-rose-700">
                            <Trash2 size={12} />
                          </Button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent></Card>

          {r.notes && (
            <Card><CardContent className="p-5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#b8862d] mb-1.5">Notes</h3>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{r.notes}</p>
            </CardContent></Card>
          )}
          {r.rejection_reason && (
            <Card><CardContent className="p-5 bg-rose-50 border border-rose-200">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-rose-700 mb-1.5">Rejection reason</h3>
              <p className="text-sm text-rose-900 whitespace-pre-wrap">{r.rejection_reason}</p>
            </CardContent></Card>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <Card><CardContent className="p-5 space-y-3 text-sm">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#b8862d]">Status</h3>
            <Badge className={`text-xs ${STATUS_TONE[r.status]}`}>{r.status.replace(/_/g, ' ')}</Badge>
            <div className="text-xs text-slate-500 space-y-1">
              <div>Submitted: {new Date(r.submitted_at).toLocaleDateString()}</div>
              {r.activation_date && <div>Activated: {r.activation_date}</div>}
              {r.qualification_end_date && <div>Window ends: {r.qualification_end_date}</div>}
              {r.finalized_at && <div>Finalized: {new Date(r.finalized_at).toLocaleDateString()}</div>}
              {r.bonus_payment_id && <div className="text-emerald-700">Payment: {r.bonus_payment_id}</div>}
            </div>
            <div className="border-t border-slate-200 pt-3 text-xs text-slate-500 space-y-1">
              <div><strong>Referrer:</strong> {r.referrer_name}</div>
              <div>NPN: {r.referrer_npn}</div>
              {r.submitted_on_behalf && (
                <div className="mt-2 rounded bg-violet-50 border border-violet-200 px-2 py-1.5 text-violet-900" data-testid="ref-detail-on-behalf">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700">Registered on behalf</div>
                  <div className="flex items-center gap-2 mt-1">
                    <UserAvatar userId={r.submitted_by_user_id} name={r.submitted_by_name} size="sm" />
                    <span>{r.submitted_by_name || '—'}</span>
                  </div>
                  <div className="text-[10.5px] text-violet-700 mt-1">Bonus (if earned) still pays to the referrer above.</div>
                </div>
              )}
            </div>
          </CardContent></Card>

          {isKR && r.status === 'pending_approval' && (
            <Card><CardContent className="p-5 space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#b8862d]">Actions</h3>
              <Button onClick={() => setShowApprove(true)} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn">
                <Check size={14} className="mr-1.5" /> Approve
              </Button>
              <Button onClick={() => setShowReject(true)} variant="outline" className="w-full border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="reject-btn">
                <X size={14} className="mr-1.5" /> Reject
              </Button>
            </CardContent></Card>
          )}

          {isKR && r.status === 'active' && (
            <Card><CardContent className="p-5 space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#b8862d]">Actions</h3>
              <Button onClick={doFinalize} disabled={finalizing} className="w-full bg-[#0f2a5e] hover:bg-[#0a1f47]" data-testid="finalize-btn">
                {finalizing ? 'Finalizing…' : 'Finalize & pay bonus'}
              </Button>
              <p className="text-[11px] text-slate-500">
                Finalizing locks the totals and creates a bonus payment in Accounting.
              </p>
            </CardContent></Card>
          )}

          {/* Recruit progress pipeline — visible to everyone,
              editable only for KR admin. Days-since-submit clock
              starts on `submitted_at`. */}
          <RecruitProgressTimeline
            referral={r}
            stages={stages}
            isKR={isKR}
            onStageChange={updateStage}
          />

          {/* Signed agreement evidence (electronic signature + on-behalf) */}
          <AgreementEvidenceCard referral={r} />
        </aside>
      </div>

      {/* Approve dialog */}
      <Dialog open={showApprove} onOpenChange={setShowApprove}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve referral</DialogTitle>
            <DialogDescription>
              Set the activation date. The 75-day qualification window starts on that date.
              Leave blank to use today.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs">Activation date (optional)</Label>
            <Input type="date" value={approvalDate} onChange={(e) => setApprovalDate(e.target.value)} data-testid="approve-activation-date" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowApprove(false)} disabled={approving}>Cancel</Button>
            <Button onClick={doApprove} disabled={approving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-confirm">
              {approving ? 'Approving…' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject referral</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs">Reason *</Label>
            <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowReject(false)} disabled={rejecting}>Cancel</Button>
            <Button onClick={doReject} disabled={rejecting || !rejectReason.trim()} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="reject-confirm">
              {rejecting ? 'Rejecting…' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add policy */}
      <AddPolicyDialog
        open={showAddPolicy}
        onOpenChange={setShowAddPolicy}
        referralId={referralId}
        onCreated={() => { setShowAddPolicy(false); load(); }}
      />
    </div>
  );
}

function Stat({ icon, label, value, highlight = false }) {
  return (
    <div className={`p-3 rounded border ${highlight ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
      <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">
        {icon} {label}
      </div>
      <div className={`text-xl font-semibold ${highlight ? 'text-emerald-700' : 'text-slate-900'}`}>{value}</div>
    </div>
  );
}

/* ─── Recruit Progress Timeline ──────────────────────── */
function RecruitProgressTimeline({ referral, stages, isKR, onStageChange }) {
  const currentStage = referral.recruit_stage || 'submitted';
  const history = referral.recruit_stage_history || [];
  const daysSinceSubmit = referral.days_since_submission;
  const [showAdvance, setShowAdvance] = useState(false);
  const [nextStage, setNextStage] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const stageIndex = stages.findIndex((s) => s.key === currentStage);
  const historyByStage = useMemo(() => {
    const map = {};
    history.forEach((h) => {
      // Keep the most recent transition entry per stage (in case a stage
      // was walked back and forth) so the timeline shows the freshest date.
      map[h.stage] = h;
    });
    return map;
  }, [history]);

  const doAdvance = async () => {
    if (!nextStage) return;
    setSaving(true);
    try {
      await onStageChange(nextStage, note.trim() || null);
      toast.success(`Stage updated to ${stages.find(s => s.key === nextStage)?.label || nextStage}`);
      setShowAdvance(false);
      setNextStage(''); setNote('');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Stage update failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="recruit-progress-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#b8862d]">Recruit Progress</h3>
          {typeof daysSinceSubmit === 'number' && (
            <div className="text-[10.5px] text-slate-500 flex items-center gap-1" data-testid="days-since-submit">
              <CalendarIcon size={11} />
              Day {daysSinceSubmit} since submission
            </div>
          )}
        </div>

        <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3">
          {stages.map((s, i) => {
            const reached = i <= stageIndex;
            const isCurrent = s.key === currentStage;
            const entry = historyByStage[s.key];
            return (
              <li key={s.key} className="pl-4 relative" data-testid={`stage-row-${s.key}`}>
                <span
                  className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 ${reached
                    ? (isCurrent ? 'bg-[#0f2a5e] border-[#0f2a5e] ring-4 ring-[#0f2a5e]/15' : 'bg-emerald-500 border-emerald-500')
                    : 'bg-white border-slate-300'}`}
                />
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className={`text-sm font-semibold ${reached ? 'text-slate-900' : 'text-slate-400'}`}>
                    {s.label}
                  </span>
                  {isCurrent && (
                    <Badge className="bg-[#0f2a5e] text-white text-[9px] px-1.5 py-0">CURRENT</Badge>
                  )}
                </div>
                <div className={`text-[11px] ${reached ? 'text-slate-500' : 'text-slate-400'}`}>
                  {s.description}
                </div>
                {entry && (
                  <div className="text-[10.5px] text-slate-500 mt-0.5 flex items-center gap-1">
                    <Check size={9} className="text-emerald-500" />
                    {new Date(entry.changed_at).toLocaleDateString()} · {entry.changed_by_name || '—'}
                    {entry.note && <span className="italic">· {entry.note}</span>}
                  </div>
                )}
              </li>
            );
          })}
        </ol>

        {isKR && (
          <div className="pt-3 border-t border-slate-100">
            {!showAdvance ? (
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                onClick={() => { setShowAdvance(true); setNextStage(''); }}
                data-testid="advance-stage-btn"
              >
                <MoveRight size={12} className="mr-1.5" /> Update stage
              </Button>
            ) : (
              <div className="space-y-2">
                <div>
                  <Label className="text-[11px]">Move to stage</Label>
                  <Select value={nextStage} onValueChange={setNextStage}>
                    <SelectTrigger data-testid="next-stage-select">
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {stages.filter(s => s.key !== currentStage).map(s => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px]">Note (optional)</Label>
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. spoke with candidate — interview set for Friday" data-testid="stage-note-input" />
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" className="flex-1" onClick={() => setShowAdvance(false)} disabled={saving}>Cancel</Button>
                  <Button size="sm" onClick={doAdvance} disabled={saving || !nextStage} className="flex-1 bg-[#0f2a5e] hover:bg-[#0a1f47]" data-testid="stage-save-btn">
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Signed agreement evidence ──────────────────────── */
function AgreementEvidenceCard({ referral }) {
  if (!referral.agreement_accepted) return null;
  const signedOn = referral.agreement_signed_at
    ? new Date(referral.agreement_signed_at).toLocaleString()
    : '—';
  return (
    <Card data-testid="agreement-evidence-card">
      <CardContent className="p-5 space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[#b8862d] flex items-center gap-1.5">
          <FileSignature size={12} /> Signed Agreement
        </h3>
        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-0.5">Electronic signature</div>
          <div className="font-serif text-lg italic text-slate-900" data-testid="agreement-signature-preview">
            {referral.agreement_signature || '—'}
          </div>
        </div>
        <div className="text-[11px] text-slate-500 space-y-0.5">
          <div><strong>Version:</strong> {referral.agreement_version || '1.0'}</div>
          <div><strong>Signed at:</strong> {signedOn}</div>
          {referral.agreement_signature_ip && (
            <div><strong>Signed from:</strong> <span className="font-mono">{referral.agreement_signature_ip}</span></div>
          )}
        </div>
        {referral.submitted_on_behalf && (
          <div className="mt-2 rounded bg-violet-50 border border-violet-200 px-2 py-2 space-y-1 text-[11px]" data-testid="on-behalf-evidence">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700">Admin-on-behalf certification</div>
            <div className="text-violet-900">
              <strong>Method:</strong>{' '}
              {(ACCEPTANCE_METHODS.find(m => m.value === referral.on_behalf_acceptance_method)?.label) || referral.on_behalf_acceptance_method || '—'}
            </div>
            {referral.on_behalf_acceptance_notes && (
              <div className="text-violet-900 italic">&ldquo;{referral.on_behalf_acceptance_notes}&rdquo;</div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function AddPolicyDialog({ open, onOpenChange, referralId, onCreated }) {
  const [form, setForm] = useState({ product: 'medicare', member_name: '', policy_number: '', active: true, issued_at: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.issued_at) delete payload.issued_at;
      if (!payload.member_name) delete payload.member_name;
      if (!payload.policy_number) delete payload.policy_number;
      await referralAPI.addPolicy(referralId, payload);
      toast.success('Policy added');
      setForm({ product: 'medicare', member_name: '', policy_number: '', active: true, issued_at: '' });
      onCreated();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to add policy'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-policy-dialog">
        <DialogHeader>
          <DialogTitle>Add policy to referral</DialogTitle>
          <DialogDescription className="text-xs">
            Only active, issued, in-force policies count toward the bonus.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <div>
            <Label className="text-xs">Product *</Label>
            <Select value={form.product} onValueChange={(v) => setForm(f => ({ ...f, product: v }))}>
              <SelectTrigger data-testid="policy-product"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PRODUCT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Member name</Label>
              <Input value={form.member_name} onChange={(e) => setForm(f => ({ ...f, member_name: e.target.value }))} data-testid="policy-member" />
            </div>
            <div>
              <Label className="text-xs">Policy #</Label>
              <Input value={form.policy_number} onChange={(e) => setForm(f => ({ ...f, policy_number: e.target.value }))} data-testid="policy-number" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Issued date</Label>
            <Input type="date" value={form.issued_at} onChange={(e) => setForm(f => ({ ...f, issued_at: e.target.value }))} data-testid="policy-issued" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="policy-active" checked={form.active} onCheckedChange={(v) => setForm(f => ({ ...f, active: !!v }))} data-testid="policy-active" />
            <Label htmlFor="policy-active" className="text-xs cursor-pointer">Policy is currently active & in-force</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0f2a5e] hover:bg-[#0a1f47]" data-testid="policy-submit">
            {saving ? 'Adding…' : 'Add policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
